import React, { useEffect, useState, useCallback, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ConfluenceLogo } from '@atlaskit/logo';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';

import { useChoreographerAssets } from '@confluence/choreographer-services';
import { useSessionData } from '@confluence/session-data';
import {
	END_OF_TRIAL_MODAL_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';

import {
	LeftSide,
	EndOfTrialModalHeading,
	RightSide,
	BenefitsList,
	ButtonsContainer,
	EndOfTrialModalBody,
} from './styled/StyledModal';
import { Carousel } from './components/carousel/Carousel';
import { EndOfTrialSubtitle } from './components/EndOfTrialSubtitle';
import { AddPaymentDetailsButton } from './components/AddPaymentDetailsButton';
import { MaybeLaterButton } from './components/MaybeLaterButton';
import { DeactivationWarning } from './components/DeactivationWarning';
import { UnlimitedUsers } from './components/benefits/UnlimitedUsers';
import { PageInsights } from './components/benefits/PageInsights';
import { PageRestrictions } from './components/benefits/PageRestrictions';
import { GuestAccess } from './components/benefits/GuestAccess';
import { useUserInfo, useUserInfoActions } from './store/userInfoStore';
import { useFeatureUsage } from './store/featureUsageStore';
import { Timeline } from './components/timeline/Timeline';
import { useFireExposureEvents } from './hooks/analytics-handlers/useFireExposureEvents';
import { useCloseModalEvents } from './hooks/analytics-handlers/useCloseModalEvents';
import { useExperimentInfo } from './store/experimentInfoStore';
import { useOperationalInfo } from './store/operationalStore';

const i18n = defineMessages({
	modalTitleDaysLeft: {
		id: 'end-of-trial-payment-modals.standard.title.days-left',
		defaultMessage: 'Your trial ends in {daysLeft, number} days',
		description: 'Modal title when 7-2 days still left in trial',
	},
	modalTitle1DayLeft: {
		id: 'end-of-trial-payment-modals.standard.title.1-day-left',
		defaultMessage: 'Your trial ends tomorrow',
		description: 'Modal title when trial ends tomorrow',
	},
});

export const EndOfTrialModal = () => {
	const [isOpen, setIsOpen] = useState(true);
	const { cohort } = useExperimentInfo();
	const { loading } = useOperationalInfo();
	const { setHasModalBeenSeen } = useUserInfoActions();
	const { fireExposureEvents } = useFireExposureEvents();
	const { fireCloseModalEvents } = useCloseModalEvents();
	const { daysLeftInTrial, billingSourceSystem, modalKey } = useUserInfo();
	const { numberOfUsers } = useFeatureUsage();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { edition } = useSessionData();
	const intl = useIntl();
	const { Modal } = useChoreographerAssets();

	useEffect(() => {
		if (!loading) {
			void fireExposureEvents();
			void fireCloseModalEvents();
			experienceTracker.succeed({
				name: END_OF_TRIAL_MODAL_EXPERIENCE,
				attributes: {
					edition,
					daysLeftInTrial,
					cohort,
					billingSourceSystem,
					modalKey,
				},
			});
		}
	}, [
		fireCloseModalEvents,
		cohort,
		fireExposureEvents,
		loading,
		experienceTracker,
		edition,
		daysLeftInTrial,
		billingSourceSystem,
		modalKey,
	]);

	useEffect(() => {
		void setHasModalBeenSeen(true);
	}, [setHasModalBeenSeen]);

	const closeModal = useCallback(() => {
		setIsOpen(false);
	}, []);

	// fires exposure event
	useEffect(() => {
		if (edition && daysLeftInTrial) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'feature',
					actionSubjectId: 'end-of-trial-payment-modals',
					attributes: {
						edition,
						daysLeftInTrial,
						cohort: 'treatment',
					},
					source: 'endOfTrialModal',
				},
			}).fire();
		}
	}, [createAnalyticsEvent, edition, daysLeftInTrial]);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={closeModal} width="x-large">
					<EndOfTrialModalBody>
						<LeftSide>
							<ConfluenceLogo appearance="brand" />
							<EndOfTrialModalHeading>
								{daysLeftInTrial === 1
									? intl.formatMessage(i18n.modalTitle1DayLeft)
									: intl.formatMessage(i18n.modalTitleDaysLeft, {
											daysLeft: daysLeftInTrial,
										})}
							</EndOfTrialModalHeading>
							<EndOfTrialSubtitle />
							{daysLeftInTrial !== 3 ? (
								<>
									<BenefitsList>
										<UnlimitedUsers />
										<PageInsights />
										<PageRestrictions />
										<GuestAccess />
										{numberOfUsers && numberOfUsers > 10 && <DeactivationWarning />}
									</BenefitsList>
								</>
							) : (
								<Timeline />
							)}
							<ButtonsContainer>
								<MaybeLaterButton closeModal={closeModal} />
								<AddPaymentDetailsButton closeModal={closeModal} />
							</ButtonsContainer>
						</LeftSide>
						<RightSide>
							<Carousel />
						</RightSide>
					</EndOfTrialModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
};
